import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, Wallet, Eye, EyeOff, ArrowLeft, Mail, RefreshCw } from "lucide-react";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

type Screen = "auth" | "reset" | "pending-verification";

export default function Home() {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [screen, setScreen] = useState<Screen>("auth");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPw, setShowLoginPw] = useState(false);
  // Track if login was blocked due to unverified email
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [showRegPw, setShowRegPw] = useState(false);
  const [regAcceptsLegal, setRegAcceptsLegal] = useState(false);

  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(0);

  const utils = trpc.useUtils();

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const loginMutation = trpc.auth.local.loginEmail.useMutation({
    onSuccess: async () => {
      // Do NOT call toast.success() here - Sonner 2.x uses ReactDOM.flushSync()
      // internally when adding toasts. Calling flushSync during the same tick as
      // utils.auth.me.invalidate() causes React to try inserting DOM nodes that
      // are being modified by the route transition, causing the insertBefore error.
      // Do NOT call navigate() here — the useEffect below handles redirect
      // when isAuthenticated becomes true after invalidate().
      // Calling navigate() during the same tick as invalidate() causes
      // React to try inserting DOM nodes that no longer exist (insertBefore).
      await utils.auth.me.invalidate();
    },
    onError: (err) => {
      if (err.message === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(loginEmail);
        setScreen("pending-verification");
      } else if (err.message === "NO_LOCAL_CREDENTIAL") {
        toast.error("Esta conta usa acesso pelo Google. Entre pelo botão ‘Entrar com Google’. ");
      } else if (err.message === "INVITE_ONLY") {
        toast.error("Acesso restrito. Seu e-mail não está na lista de testadores.");
      } else if (err.message === "ACCOUNT_SUSPENDED") {
        toast.error("Seu acesso está temporariamente suspenso. Entre em contato com o atendimento do Bolso.");
      } else {
        toast.error(err.message);
      }
    },
  });

  const registerMutation = trpc.auth.local.register.useMutation({
    onSuccess: (data, variables) => {
      if (data.requiresVerification) {
        // Usuários comuns precisam confirmar o endereço antes de entrar.
        setUnverifiedEmail(variables.email);
        setScreen("pending-verification");
        return;
      }

      // Testadores convidados entram imediatamente, sem depender da entrega
      // do e-mail de confirmação.
      void utils.auth.me.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const resetMutation = trpc.auth.local.requestPasswordReset.useMutation({
    onSuccess: () => setResetSent(true),
    onError: (err) => toast.error(err.message),
  });

  const resendMutation = trpc.auth.local.resendVerification.useMutation({
    onSuccess: () => {
      toast.success("E-mail de verificação reenviado! Verifique sua caixa de entrada.");
      setResendCooldown(60); // 60 segundos de cooldown
    },
    onError: (err) => toast.error(err.message),
  });

  // Mostrar erro de acesso restrito vindo de OAuth (redirect com ?error=invite_only)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error") === "invite_only") {
      toast.error("Acesso restrito. Seu e-mail não está na lista de testadores.");
      // Limpar o parâmetro da URL sem reload
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("error") === "account_suspended") {
      toast.error("Seu acesso está temporariamente suspenso. Entre em contato com o atendimento do Bolso.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Já autenticado → redirecionar para o dashboard.
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // When loading OR already authenticated, render a stable spinner.
  // Do NOT render the login form while loading or authenticated — it causes
  // DOM swaps during React commit (insertBefore error).
  // The useEffect above navigates to /dashboard once isAuthenticated=true.
  if (loading || isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return toast.error("Preencha todos os campos");
    loginMutation.mutate({ email: loginEmail, password: loginPassword });
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword || !regConfirm) return toast.error("Preencha todos os campos");
    if (regPassword !== regConfirm) return toast.error("As senhas não coincidem");
    if (regPassword.length < 6) return toast.error("A senha deve ter pelo menos 6 caracteres");
    if (!regAcceptsLegal) return toast.error("Leia e aceite os Termos de Uso e a Política de Privacidade para continuar");
    registerMutation.mutate({ name: regName, email: regEmail, password: regPassword, acceptsLegalTerms: true });
  };

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return toast.error("Informe seu e-mail");
    resetMutation.mutate({ email: resetEmail });
  };

  const handleResend = () => {
    if (!unverifiedEmail || resendCooldown > 0) return;
    resendMutation.mutate({ email: unverifiedEmail });
  };

  // ── Tela: E-mail Pendente de Verificação ─────────────────────────────────
  if (screen === "pending-verification") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <div
              className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg"
              style={{ boxShadow: "0 8px 32px oklch(0.38 0.12 150 / 0.3)" }}
            >
              <Wallet className="h-8 w-8 text-primary-foreground" />
            </div>
            <h1
              className="text-4xl font-bold text-foreground tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Bolso
            </h1>
          </div>

          <Card className="shadow-md border-border/60">
            <CardHeader className="pb-2 text-center">
              <div className="flex justify-center mb-3">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Mail className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <CardTitle className="text-xl">Verifique seu e-mail</CardTitle>
              <CardDescription className="text-sm leading-relaxed mt-1">
                Enviamos um link de confirmação para{" "}
                <strong className="text-foreground">{unverifiedEmail}</strong>.
                <br />
                Clique no link para ativar sua conta.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="rounded-lg bg-muted/50 border border-border/40 p-3 text-xs text-muted-foreground leading-relaxed">
                <p>📬 Não recebeu o e-mail? Verifique também a pasta de <strong>spam</strong> ou <strong>lixo eletrônico</strong>.</p>
              </div>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleResend}
                disabled={resendMutation.isPending || resendCooldown > 0}
              >
                {resendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {resendCooldown > 0
                  ? `Reenviar em ${resendCooldown}s`
                  : "Reenviar e-mail de verificação"}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setScreen("auth");
                  setUnverifiedEmail("");
                }}
                className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                <ArrowLeft className="h-4 w-4" /> Voltar ao login
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }


  // ── Tela: Recuperação de Senha ────────────────────────────────────────────
  if (screen === "reset") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-3 shadow-lg">
              <Wallet className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>Bolso</h1>
          </div>

          <Card className="shadow-md border-border/60">
            <CardHeader className="pb-3">
              <button
                onClick={() => { setScreen("auth"); setResetSent(false); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Voltar ao login
              </button>
              <CardTitle className="text-xl">Recuperar senha</CardTitle>
              <CardDescription>Informe seu e-mail para receber as instruções</CardDescription>
            </CardHeader>
            <CardContent>
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">📬</div>
                  <p className="text-sm text-muted-foreground">
                    Se este e-mail estiver cadastrado, você receberá as instruções em breve.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-email">E-mail</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={resetMutation.isPending}>
                    {resetMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Enviar instruções
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Tela Principal: Login / Cadastro ──────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg" style={{ boxShadow: "0 8px 32px oklch(0.38 0.12 150 / 0.3)" }}>
            <Wallet className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Bolso
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Controle financeiro pessoal</p>
        </div>

        {/* Auth Card */}
        <Card className="shadow-md border-border/60">
          <CardContent className="pt-6">
            <div className="mb-5 rounded-xl border border-primary/20 bg-primary/5 p-4 text-center">
              <p className="text-sm font-semibold text-foreground">A forma mais fácil de começar</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Use sua conta Google. Não é preciso criar uma senha do Bolso nem receber convite.</p>
              <Button
                type="button"
                className="mt-3 w-full bg-primary hover:bg-primary/90"
                onClick={() => { window.location.href = "/api/auth/google"; }}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Entrar com Google
              </Button>
            </div>

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Entrar</TabsTrigger>
                <TabsTrigger value="register">Cadastrar</TabsTrigger>
              </TabsList>

              {/* ── Login Tab ── */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email">E-mail</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password">Senha</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showLoginPw ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        autoComplete="current-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPw(!showLoginPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showLoginPw ? "Ocultar senha" : "Mostrar senha"}
                        aria-pressed={showLoginPw}
                      >
                        {showLoginPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setScreen("reset")}
                      className="text-xs text-primary hover:underline"
                    >
                      Esqueci minha senha
                    </button>
                  </div>
                  <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                    {loginMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Entrar
                  </Button>
                </form>
              </TabsContent>

              {/* ── Register Tab ── */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-name">Nome completo</Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder="Seu nome"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      autoComplete="name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-email">E-mail</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-password">Senha</Label>
                    <div className="relative">
                      <Input
                        id="reg-password"
                        type={showRegPw ? "text" : "password"}
                        placeholder="Mínimo 6 caracteres"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        autoComplete="new-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPw(!showRegPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showRegPw ? "Ocultar senha" : "Mostrar senha"}
                        aria-pressed={showRegPw}
                      >
                        {showRegPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-confirm">Confirmar senha</Label>
                    <Input
                      id="reg-confirm"
                      type="password"
                      placeholder="Repita a senha"
                      value={regConfirm}
                      onChange={(e) => setRegConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border/60 p-3 text-xs leading-relaxed text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={regAcceptsLegal}
                      onChange={(event) => setRegAcceptsLegal(event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    />
                    <span>Li e aceito os <Link to="/termos-de-uso" target="_blank" className="font-medium text-primary underline underline-offset-2">Termos de Uso</Link> e a <Link to="/politica-de-privacidade" target="_blank" className="font-medium text-primary underline underline-offset-2">Política de Privacidade</Link>.</span>
                  </label>
                  <Button type="submit" className="w-full mt-1" disabled={registerMutation.isPending}>
                    {registerMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Criar conta
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Outras formas de acesso */}
            <div className="mt-5">
              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                  ou use outra conta
                </span>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  variant="outline"
                  className="w-full bg-card hover:bg-muted/50"
                  onClick={() => { window.location.href = getLoginUrl(); }}
                >
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" rx="6" fill="#1a1a2e"/>
                    <path d="M7 8l5 8 5-8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Entrar com Manus
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-3 gap-3 mt-6 text-center">
          {[
            { icon: "💰", label: "Controle total" },
            { icon: "🤖", label: "IA integrada" },
            { icon: "📊", label: "Visão clara" },
          ].map((f) => (
            <div key={f.label} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-card/60 border border-border/40">
              <span className="text-xl">{f.icon}</span>
              <span className="text-xs text-muted-foreground font-medium">{f.label}</span>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-5">
          O Bolso organiza informações que você registra. Não se conecta a bancos nem movimenta dinheiro.
        </p>
        <p className="mt-2 text-center text-xs text-muted-foreground"><Link to="/termos-de-uso" className="underline underline-offset-2 hover:text-foreground">Termos de Uso</Link><span className="mx-2">·</span><Link to="/politica-de-privacidade" className="underline underline-offset-2 hover:text-foreground">Privacidade</Link></p>
      </div>
    </div>
  );
}
